import { useRouterState } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import { useButtonSound } from '@/shared/lib/audio';
import { useResolvedLayout } from '@/shared/lib/layout';
import { PageTransition } from './motion';

export type AppShellProps = {
  children: ReactNode;
};

/**
 * Persistent navigation shell wrapping every route. Renders just the
 * animated outlet — no top strip, no settings access. Pages own their
 * own chrome (back buttons, settings pills, etc).
 *
 * Sets `data-layout` on `<html>` from the resolved layout so CSS rules
 * keyed on viewport (e.g. `html[data-layout=desktop] .ring`) cascade.
 */
export const AppShell = ({ children }: AppShellProps) => {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const layout = useResolvedLayout();

  useButtonSound();

  useEffect(() => {
    document.documentElement.setAttribute('data-layout', layout);
    return () => {
      document.documentElement.removeAttribute('data-layout');
    };
  }, [layout]);

  return (
    <main className="relative h-full overflow-hidden bg-bg text-text">
      <PageTransition routeKey={pathname}>{children}</PageTransition>
    </main>
  );
};
